"use client";
import React, { useEffect, useRef, useState } from 'react';
import { motion } from 'framer-motion';

const CLASH = 'var(--font-clash), "Clash Display", sans-serif';
const MANROPE = 'var(--font-manrope), Manrope, sans-serif';
const INK = '#080400';
const PURPLE = '#3730a3';

const stages = ['Attention', 'Interest', 'Consideration', 'Conversion', 'Retention'];
const STEP_MS = 1600;

function ArrowIcon({ color }: { color: string }) {
    return (
        <svg width="18" height="14" viewBox="0 0 18 14" fill="none" className="shrink-0" aria-hidden="true">
            <path d="M1 7h14M10 1.5L16.5 7 10 12.5" stroke={color} strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" style={{ transition: 'stroke 0.4s ease' }} />
        </svg>
    );
}

function FunnelArrow({ passed, traveling }: { passed: boolean; traveling: boolean }) {
    return (
        <div className="relative w-6 md:w-9 h-4 flex items-center shrink-0">
            <ArrowIcon color={passed || traveling ? PURPLE : 'rgba(8,4,0,0.2)'} />
            {traveling && (
                <motion.span
                    key="pulse"
                    className="absolute top-1/2 w-2 h-2 rounded-full"
                    style={{ background: PURPLE, boxShadow: '0 0 8px rgba(55,48,163,0.5)', marginTop: -4 }}
                    initial={{ left: '0%', opacity: 0 }}
                    animate={{ left: '85%', opacity: [0, 1, 1, 0] }}
                    transition={{ duration: STEP_MS / 1000, ease: 'linear' }}
                />
            )}
        </div>
    );
}

export default function MetaAdsFunnel() {
    const [active, setActive] = useState(0);
    const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);

    useEffect(() => {
        timerRef.current = setInterval(() => {
            setActive((prev) => (prev + 1) % stages.length);
        }, STEP_MS);
        return () => { if (timerRef.current) clearInterval(timerRef.current); };
    }, []);

    return (
        <section className="relative overflow-hidden py-20 md:py-28 px-6 md:px-16 lg:px-24" style={{ background: '#ffffff', borderTopLeftRadius: '80px', borderTopRightRadius: '80px' }}>
            <div className="absolute inset-0 pointer-events-none opacity-[0.035]" style={{ backgroundImage: 'radial-gradient(circle, rgba(55,48,163,1) 1px, transparent 1px)', backgroundSize: '40px 40px' }} />

            <div className="max-w-[1100px] mx-auto relative z-10 flex flex-col items-center text-center gap-10">
                <div className="flex flex-col items-center gap-6 max-w-[820px] mx-auto">
                    <motion.span
                        initial={{ opacity: 0, y: 10 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.5 }}
                        className="text-xs font-bold tracking-[0.2em] uppercase"
                        style={{ color: PURPLE, fontFamily: CLASH }}
                    >
                        The Journey
                    </motion.span>

                    <motion.h2
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.6, delay: 0.1 }}
                        className="text-3xl md:text-5xl font-bold tracking-tight leading-tight"
                        style={{ fontFamily: CLASH, color: INK }}
                    >
                        From Attention to Action
                    </motion.h2>

                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.6, delay: 0.2 }}
                        className="flex flex-col gap-5"
                    >
                        <p
                            className="text-base md:text-lg leading-relaxed"
                            style={{ fontFamily: MANROPE, color: 'rgba(8,4,0,0.6)', letterSpacing: '0.02em' }}
                        >
                            Meta advertising isn&apos;t simply about getting someone to stop scrolling.
                        </p>
                        <p
                            className="text-base md:text-lg leading-relaxed"
                            style={{ fontFamily: MANROPE, color: 'rgba(8,4,0,0.6)', letterSpacing: '0.02em' }}
                        >
                            The real objective is to move them through the journey:
                        </p>
                    </motion.div>
                </div>

                {/* animated funnel stages */}
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.6, delay: 0.3 }}
                    className="flex flex-nowrap items-center justify-center gap-2 md:gap-3 overflow-x-auto pb-2 -mb-2 mx-auto max-w-full"
                >
                    {stages.map((stage, i) => {
                        const isActive = i === active;
                        const isPassed = i < active;
                        return (
                            <React.Fragment key={stage}>
                                <motion.span
                                    animate={{
                                        scale: isActive ? 1.08 : 1,
                                        background: isActive ? PURPLE : isPassed ? 'rgba(55,48,163,0.08)' : 'rgba(8,4,0,0.04)',
                                        color: isActive ? '#ffffff' : isPassed ? PURPLE : 'rgba(8,4,0,0.45)',
                                        borderColor: isActive ? PURPLE : isPassed ? 'rgba(55,48,163,0.3)' : 'rgba(8,4,0,0.1)',
                                        boxShadow: isActive ? '0 0 22px rgba(55,48,163,0.35)' : '0 0 0px rgba(0,0,0,0)',
                                    }}
                                    transition={{ duration: 0.4, ease: 'easeOut' }}
                                    className="text-xs md:text-sm font-bold tracking-[0.06em] uppercase px-3.5 py-2.5 rounded-full shrink-0 whitespace-nowrap"
                                    style={{ fontFamily: CLASH, border: '1px solid' }}
                                >
                                    {stage}
                                </motion.span>
                                {i < stages.length - 1 && (
                                    <FunnelArrow passed={isPassed} traveling={isActive} />
                                )}
                            </React.Fragment>
                        );
                    })}
                </motion.div>
            </div>
        </section>
    );
}
